import moment from 'moment';
import { v4 as uuid } from 'uuid';

import constants from '@/constants';

import { setSyncStatus } from '@/store/mutation-types';
import rootApi from '@/store/api';
import { actionLog } from './namespace';
import actions from './actionTypes';
import mutations from './mutationTypes';
import api from './api';

export default {
  async [actions.createCompanyFiles]({ state, commit }, { companyId, projectId, fileEntry }) {
    /**
     * @todo It's possible that two entities with the same `companyId` will be created.
     * Should be processed at BE.
     */
    const prevEntity = state.list.find((item) => item.companyId === companyId);
    if (prevEntity != null) {
      actionLog(actions.createCompanyFiles, `Entity for companyId "${companyId}" already exists`);
      throw new Error('Failed to create Company Files because entity already exists');
    }

    const params = {
      type: 'CompanyFiles',
      companyId,
      projectFiles: {
        [projectId]: fileEntry ? [fileEntry] : [],
      },
    };
    const entity = await api.createEntity(params);
    commit(mutations.setEntity, { ...params, _id: entity.id, _rev: entity.rev });
  },

  async [actions.updateCompanyFiles]({ state, commit }, { id }) {
    const prevEntity = state.list.find(({ _id }) => _id === id);
    if (prevEntity == null) {
      actionLog(actions.updateCompanyFiles, `Missing Company Files with id "${id}"`);
      throw new Error('Failed to update Company Files because entity does not exist');
    }

    try {
      const update = await api.updateEntity(prevEntity);
      if (update) {
        const currentEntity = state.list.find(({ _id }) => _id === update.id);
        // Update revision only if it wasn't updated by someone else.
        if (currentEntity._rev === prevEntity._rev) {
          // We shouldn't update entire entity in store because someone else could have updated it.
          commit(mutations.setEntityRevId, { id: update.id, revId: update.rev });
        }
      }
    } catch (error) {
      actionLog(actions.updateCompanyFiles, `Failed to update Company Files with id "${id}"`, error);
      throw error;
    }
  },

  async [actions.addProjectFile](
    { state, commit, dispatch },
    { companyId, projectId, fileName, fileArrayBuffer, fileInfo }
  ) {
    const fileEntry = await dispatch(actions.uploadProjectFile, {
      companyId,
      projectId,
      fileName,
      fileArrayBuffer,
      fileInfo,
    });

    let updateId;
    let prevFileEntry;
    const entity = state.list.find((item) => item.companyId === companyId);
    const id = entity ? entity._id : null;
    try {
      if (entity == null) {
        await dispatch(actions.createCompanyFiles, { companyId, projectId, fileEntry });
      } else {
        updateId = uuid();
        if (
          entity.projectFiles != null &&
          entity.projectFiles[projectId] != null &&
          entity.projectFiles[projectId].files != null
        ) {
          prevFileEntry = entity.projectFiles[projectId].files.find((item) => fileName === item.fileName);
        }
        commit(mutations.setProjectFile, { id, projectId, fileEntry, updateId });
        await dispatch(actions.updateCompanyFiles, { id });
      }
    } catch (error) {
      if (entity != null) {
        /** @todo We should use something like vuex-revert */
        // In case of an error remove from entity only what we've added.
        commit(mutations.revertProjectFileUpdate, { id, projectId, updateId, prevFileEntry });
      }
      actionLog(
        actions.addProjectFile,
        `Failed to add Project file ${JSON.stringify({
          companyId,
          projectId,
          fileName,
          fileInfo,
          size: fileArrayBuffer ? fileArrayBuffer.byteLength : null,
        })}`,
        error
      );
      throw error;
      /** @todo Delete file from AWS S3 */
    }
  },

  async [actions.deleteProjectFile]({ state, commit, dispatch }, { companyId, projectId, fileName }) {
    const entity = state.list.find((item) => item.companyId === companyId);
    if (entity == null) {
      return;
    }

    const id = entity._id;
    const updateId = uuid();
    let prevFileEntry =
      entity.projectFiles[projectId] == null || entity.projectFiles[projectId].files == null
        ? undefined
        : entity.projectFiles[projectId].files.find((item) => fileName === item.fileName);
    const fileEntry = {
      fileName,
      status: constants.companyFiles.statuses.NOT_LOADED,
    };
    if (prevFileEntry != null) {
      fileEntry.s3Key = prevFileEntry.s3Key;
      fileEntry.deleteFromS3 = true;
    }

    try {
      commit(mutations.setProjectFile, { id, projectId, fileEntry, updateId });
      await dispatch(actions.updateCompanyFiles, { id });
    } catch (error) {
      /** @todo We should use something like vuex-revert */
      // In case of an error remove from entity only what we've added.
      commit(mutations.revertProjectFileUpdate, { id, projectId, updateId, prevFileEntry });
      actionLog(
        actions.deleteProjectFile,
        `Failed to delete Project file ${JSON.stringify({ companyId, projectId, fileName })}`,
        error
      );
      throw error;
    }
  },

  async [actions.changeProjectFileStatus](
    { state, commit, dispatch, getters, rootGetters },
    { companyId, projectId, fileName, status }
  ) {
    const prevEntity = state.list.find((item) => item.companyId === companyId);
    if (prevEntity == null) {
      actionLog(actions.changeProjectFileStatus, `Missing entity for companyId "${companyId}"`);
      throw new Error('Failed to update Company Files because entity does not exist');
    }

    const prevFileEntry =
      prevEntity.projectFiles[projectId] &&
      prevEntity.projectFiles[projectId].files &&
      prevEntity.projectFiles[projectId].files.find((item) => fileName === item.fileName);
    if (prevFileEntry == null) {
      actionLog(
        actions.changeProjectFileStatus,
        `File with file name "${fileName}" doesn't exist for companyId "${companyId}"`
      );
      throw new Error('Failed to update Company Files because file does not exist');
    }

    const loggedInPerson = rootGetters.loggedInPerson;
    const fileEntry = {
      ...prevFileEntry,
      status,
      approvingDate: moment().utc().format(constants.formats.dateTimeUtc),
      approvingPerson: loggedInPerson ? rootGetters.personName(loggedInPerson._id) : null,
    };

    const updateId = uuid();
    const id = prevEntity._id;
    try {
      commit(mutations.setProjectFile, { id, projectId, fileEntry, updateId });
      await dispatch(actions.updateCompanyFiles, { id });
    } catch (error) {
      /** @todo We should use something like vuex-revert */
      // In case of an error remove from entity only what we've added.
      commit(mutations.revertProjectFileUpdate, { id, projectId, updateId, prevFileEntry });
      actionLog(
        actions.changeProjectFileStatus,
        `Failed to change Project file status ${JSON.stringify({ companyId, projectId, fileName, status })}`,
        error
      );
      throw error;
    }
  },

  async [actions.changeCodeOfConductStatus]({ state, commit, dispatch, rootState }, { companyId, projectId, status }) {
    const prevEntity = state.list.find((item) => item.companyId === companyId);
    if (prevEntity == null) {
      actionLog(actions.changeCodeOfConductStatus, `Missing entity for companyId "${companyId}"`);
      throw new Error('Failed to update Code of Conduct because entity does not exist');
    }

    const id = prevEntity._id;
    try {
      commit(mutations.setProjectCodeOfConductStatus, {
        id,
        projectId,
        codeOfConduct: {
          status,
          approvedAt: moment().utc().format(constants.formats.dateTimeUtc),
          approvedBy: rootState.loggedInPerson._id,
        },
      });
      await dispatch(actions.updateCompanyFiles, { id });
    } catch (error) {
      actionLog(
        actions.changeProjectFileStatus,
        `Failed to change Project's Code of Conduct status ${JSON.stringify({ companyId, projectId, status })}`,
        error
      );
      throw error;
    }
  },

  async [actions.uploadProjectFile](_store, { companyId, projectId, fileName, fileArrayBuffer, fileInfo }) {
    let s3Key = `companies/${companyId}/projects/${projectId}/files/${fileName}`;
    if (fileInfo && fileInfo.name) {
      const parts = fileInfo.name.toLowerCase().split('.');
      if (parts.length > 1 && parts[0] !== '') {
        s3Key += `.${parts.pop()}`;
      }
    }

    try {
      await rootApi.uploadS3File({ s3Key, fileArrayBuffer });
    } catch (error) {
      actionLog(
        actions.uploadProjectFile,
        `Failed to upload Project file ${JSON.stringify({ companyId, projectId, fileName, fileInfo })}`,
        error
      );
      // throw error;
    }

    const now = moment().utc();
    const loadDate = now.format(constants.formats.dateShort);

    return {
      s3Key,
      loadDate,
      fileName,
      expiryDate: now.add(1, 'year').format(constants.formats.dateShort),
      status: constants.companyFiles.statuses.FOR_APPROVAL,
      approvingDate: null,
      approvingPerson: null,
    };
  },

  async [actions.deleteLegacyFiles]({ state, commit }, { companyId }) {
    const prevEntity = state.list.find((item) => item.companyId === companyId);
    if (prevEntity == null) {
      actionLog(actions.deleteLegacyFiles, `Missing entity for companyId "${companyId}"`);
      throw new Error('Failed to delete legacy files because entity does not exist');
    }

    try {
      const update = await api.updateEntity({ ...prevEntity, deleteLegacyFiles: true });
      if (update) {
        const currentEntity = state.list.find(({ _id }) => _id === update.id);
        // Update revision only if it wasn't updated by someone else.
        if (currentEntity._rev === prevEntity._rev) {
          // We shouldn't update entire entity in store because someone else could have updated it.
          commit(mutations.setEntityRevId, { id: update.id, revId: update.rev });
        }
      }
    } catch (error) {
      actionLog(actions.deleteLegacyFiles, `Failed to delete legacy filesLink for companyId "${companyId}"`, error);
      throw error;
    }
  },

  async [actions.syncCompanyFilesList]({ commit }) {
    commit(
      setSyncStatus,
      { status: 'Active', live: 0, message: 'Loading company files', progress: 30 },
      { root: true }
    );
    const list = await api.fetchEntities();
    commit(mutations.setList, { list });
  },

  async [actions.syncCompanyFiles]({ commit }, { entity }) {
    commit(mutations.setEntity, { entity });
  },
};



// WEBPACK FOOTER //
// ./src/store/companyFiles/actions.js